import { Controller } from "@hotwired/stimulus";
import {
  getSanitationForms,
  getSanitationForm,
} from "../../db/sanitationForms.js";
import { capitalize } from "../../utils.js";
import Mustache from "mustache";
import { getRouteDatapoints } from "../../db/routeDatapoints.js";
import {
  buildCompletedRouteProgress,
  buildInProgressRouteProgress,
  formatRouteStartedAt,
  monitorAvatarFields,
  sortRoutesByRecent,
} from "../../routeLauncherCards.js";
import {
  deleteSanitationRoute,
  getCurrentDatapointUrl,
  getSanitationRoute,
  getInProgressSanitationRoutes,
  getCompletedSanitationRoutes,
  getVisibleSanitationRoutesForOtherFarms,
  markSanitationRouteAsActive,
} from "../../db/sanitationRoutes.js";
import {
  checkOnlineStatus,
  quickCheckOnlineStatus,
} from "../../onlineStatus.js";
import {
  ensureOfflineSanitationFormsReady,
  syncPlanStaleFormIds,
} from "../../offlineSanitationForms.js";
import {
  cachedRouteDocumentExists,
  navigateToRouteDocument,
  routeNavigationGuard,
  setRouteStartButtonLoading,
} from "../../routeNavigation.js";
import { dbOpenError } from "../../db/index.js";
import { DebugLogger } from "../../db/debugLogger.js";
import { reportDiagnosticsIfDue } from "../../db/diagnosticsReporter.js";

const SYNC_STATUS_CLASSES = {
  info: "alert-info",
  success: "alert-success",
  warning: "alert-warning",
};
const deletingRouteIds = new Set();
let deleteOperationInProgress = false;

// Connects to data-controller="route-launcher--index"
export default class extends Controller {
  static targets = [
    "listContainer",
    "localRoutesCardBody",
    "routesSectionsWrapper",
    "emptyRoutesPlaceholder",
    "completedRoutesSection",
    "inProgressRoutesSection",
    "sanitationRoutesListContainer",
    "completedRoutesListContainer",
    "inProgressRoutesListContainer",
    "listItemTemplate",
    "inProgressItemTemplate",
    "completedItemTemplate",
    "emptyRoutesTemplate",
    "dbErrorTemplate",
    "loadingSpinner",
    "deleteSanitationRouteButton",
    "syncStatus",
    "syncStatusText",
    "continueRouteButton",
  ];
  static values = {
    farmId: Number,
    farmTimeZone: String,
    offlineManifest: Object,
    progressTreeLabel: String,
    progressSamplesLabel: String,
    progressSamplesWithTreeLabel: String,
    progressCompletedReadyLabel: String,
    checkingOfflineFormsMessage: String,
    syncingFormsMessage: String,
    formsReadyOfflineMessage: String,
    formsSyncFailedMessage: String,
    offlineMessage: String,
    downloadFailedMessage: String,
    routeNotFullyDownloadedMessage: String,
    resyncingLocalRoutesMessage: String,
    localRoutesResyncedMessage: String,
    localRoutesNoneFoundMessage: String,
    localRoutesOtherFarmWarningMessage: String,
    deleteRouteConfirmationMessage: String,
    deleteRouteInProgressStatus: String,
    deleteRouteCompletedStatus: String,
    deleteRouteUnknownValue: String,
    deleteRouteStartedAtLabel: String,
    deleteRouteCompletedAtLabel: String,
  };

  async connect() {
    this.boundHandleRouteDeleteClick = this.handleRouteDeleteClick.bind(this);
    this.boundHandleRouteUploaded = this.handleRouteUploaded.bind(this);
    this.boundPageShow = this.onPageShow.bind(this);
    this.element.addEventListener("click", this.boundHandleRouteDeleteClick, {
      capture: true,
    });
    this.element.addEventListener(
      "sanitation-route:uploaded",
      this.boundHandleRouteUploaded,
    );
    window.addEventListener("pageshow", this.boundPageShow);

    // Independent of the Dexie reads below (localStorage only) — diagnostics
    // are most valuable exactly when the local DB is unreadable.
    reportDiagnosticsIfDue(this.farmId).catch(() => {});

    let localSanitationForms;
    try {
      localSanitationForms = await getSanitationForms(this.farmId);
      await this.loadAndRenderLocalRoutes();
    } catch (error) {
      // Reads reject when the IndexedDB failed to open (post-crash corruption,
      // version rollback). This must never look like "no routes" — users would
      // assume their offline work is gone.
      DebugLogger.error("Route launcher failed to read local data", {
        error: error?.message,
        dbOpenError: dbOpenError?.message,
      });
      this.renderDbErrorState();
      return;
    }

    this.renderSanitationForms(localSanitationForms);
    this.synchronizeSanitationForms(localSanitationForms).catch((error) => {
      console.error("Error syncing offline forms:", error);
      this.showSyncStatus("warning", this.formsSyncFailedMessageValue);
    });
  }

  disconnect() {
    if (this.boundHandleRouteDeleteClick) {
      this.element.removeEventListener(
        "click",
        this.boundHandleRouteDeleteClick,
        {
          capture: true,
        },
      );
    }

    if (this.boundHandleRouteUploaded) {
      this.element.removeEventListener(
        "sanitation-route:uploaded",
        this.boundHandleRouteUploaded,
      );
    }

    if (this.boundPageShow) {
      window.removeEventListener("pageshow", this.boundPageShow);
    }
  }

  // Swipe-back can restore this page from bfcache after "Start route" left a
  // button spinning and before connect() can run again. Refresh local cards
  // and clear start-button loading so the launcher matches IndexedDB.
  onPageShow(event) {
    this.resetStartButtonsLoadingState();
    if (!event?.persisted) return;

    return this.loadAndRenderLocalRoutes().catch((error) => {
      DebugLogger.error(
        "Route launcher failed to refresh after back-forward cache restore",
        { error: error?.message },
      );
    });
  }

  resetStartButtonsLoadingState() {
    this.startButtons.forEach((button) => {
      setRouteStartButtonLoading(button, false);
      button.disabled = false;
    });
  }

  handleRouteDeleteClick(event) {
    const deleteButton = event.target.closest(".js-delete-button");
    if (!deleteButton || !this.element.contains(deleteButton)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.performLocalSanitationRouteDelete(deleteButton).catch((error) => {
      DebugLogger.error("Route launcher failed to delete local route", {
        error: error?.message,
      });
    });
  }

  handleRouteUploaded(event) {
    const routeId = Number(event.detail?.routeId);
    if (!Number.isFinite(routeId)) return;

    this.markRouteCardUploaded(routeId);
  }

  markRouteCardUploaded(routeId) {
    const routeCard = this.element.querySelector(
      `#sanitation_route_${routeId}`,
    );
    if (!routeCard) return;

    routeCard.dataset.uploaded = "true";
    routeCard.classList.add("opacity-75");

    routeCard.querySelectorAll("button").forEach((button) => {
      button.disabled = true;
    });

    routeCard.querySelectorAll("a[href]").forEach((link) => {
      link.setAttribute("aria-disabled", "true");
      link.setAttribute("tabindex", "-1");
      link.classList.add("pointer-events-none");
    });
  }

  renderDbErrorState() {
    if (!this.hasDbErrorTemplateTarget || !this.hasLocalRoutesCardBodyTarget) {
      return;
    }

    this.localRoutesCardBodyTarget.innerHTML =
      this.dbErrorTemplateTarget.innerHTML;
  }

  async readLocalSanitationRoutes() {
    const [completedSanitationRoutes, inProgressSanitationRoutes] =
      await Promise.all([
        getCompletedSanitationRoutes(this.farmId),
        getInProgressSanitationRoutes(this.farmId),
      ]);

    return { completedSanitationRoutes, inProgressSanitationRoutes };
  }

  async loadAndRenderLocalRoutes({ reportStatus = false } = {}) {
    const routeState = await this.readLocalSanitationRoutes();
    await this.renderLocalSanitationRoutes(routeState);
    await this.safelyRefreshContinueButtonsReadiness();

    if (reportStatus) {
      await this.reportLocalRouteResyncResult(routeState);
    }

    return routeState;
  }

  async resyncLocalRoutes(event) {
    if (event?.preventDefault) event.preventDefault();

    this.showSyncStatus("info", this.resyncingLocalRoutesMessage);

    try {
      await this.loadAndRenderLocalRoutes({ reportStatus: true });
    } catch (error) {
      DebugLogger.error("Route launcher failed to resync local routes", {
        error: error?.message,
        dbOpenError: dbOpenError?.message,
      });
      this.renderDbErrorState();
    }
  }

  async reportLocalRouteResyncResult({
    completedSanitationRoutes,
    inProgressSanitationRoutes,
  }) {
    const visibleRouteCount =
      completedSanitationRoutes.length + inProgressSanitationRoutes.length;

    if (visibleRouteCount > 0) {
      this.showSyncStatus(
        "success",
        this.localRoutesResyncedMessage.replace(
          "%{count}",
          visibleRouteCount,
        ),
      );
      return;
    }

    const otherFarmRoutes = await getVisibleSanitationRoutesForOtherFarms(
      this.farmId,
    );
    if (otherFarmRoutes.length > 0) {
      this.showSyncStatus(
        "warning",
        this.localRoutesOtherFarmWarningMessage.replace(
          "%{count}",
          otherFarmRoutes.length,
        ),
      );
      return;
    }

    this.showSyncStatus("info", this.localRoutesNoneFoundMessage);
  }

  showEmptyRoutesState() {
    if (!this.hasEmptyRoutesPlaceholderTarget) return;

    this.emptyRoutesPlaceholderTarget.innerHTML =
      this.emptyRoutesTemplateTarget.innerHTML;
    this.emptyRoutesPlaceholderTarget.classList.remove("hidden");

    if (this.hasRoutesSectionsWrapperTarget) {
      this.routesSectionsWrapperTarget.classList.add("hidden");
    }
  }

  updateRoutesSectionsVisibility(completedCount, inProgressCount) {
    if (this.hasEmptyRoutesPlaceholderTarget) {
      this.emptyRoutesPlaceholderTarget.classList.add("hidden");
      this.emptyRoutesPlaceholderTarget.innerHTML = "";
    }

    if (this.hasRoutesSectionsWrapperTarget) {
      this.routesSectionsWrapperTarget.classList.remove("hidden");
    }

    if (this.hasCompletedRoutesSectionTarget) {
      this.completedRoutesSectionTarget.classList.toggle(
        "hidden",
        completedCount === 0,
      );
    }

    if (this.hasInProgressRoutesSectionTarget) {
      this.inProgressRoutesSectionTarget.classList.toggle(
        "hidden",
        inProgressCount === 0,
      );
    }
  }

  reloadPage() {
    window.location.reload();
  }

  async synchronizeSanitationForms(localSanitationForms) {
    // No upfront "checking" state or online probe: when nothing is stale the
    // readiness check resolves locally and the bar appears once, already green.
    try {
      const result = await ensureOfflineSanitationFormsReady({
        farmId: this.farmId,
        manifest: this.offlineManifestValue,
        onSyncStart: (plan) => this.showSyncingStatus(plan),
      });
      this.removeSanitationForms(result.plan?.formsToDelete || []);
      this.renderSanitationForms(result.forms);
      if (result.ready) {
        this.enableStartButtons();
      } else {
        await this.refreshStartButtonsReadiness(result.forms);
      }
      await this.safelyRefreshContinueButtonsReadiness();
      if (result.ready) {
        this.showSyncStatus("success", this.formsReadyOfflineMessageValue);
      } else {
        this.showSyncStatus("warning", this.formsSyncFailedMessageValue);
      }
      return result.forms;
    } catch (error) {
      console.error("Error syncing forms:", error);
      this.showSyncStatus("warning", this.formsSyncFailedMessageValue);
      await this.refreshStartButtonsReadiness(localSanitationForms);
      await this.safelyRefreshContinueButtonsReadiness();
      return localSanitationForms;
    }
  }

  async downloadAll(event) {
    if (event?.preventDefault) event.preventDefault();
    this.showSyncStatus("info", this.checkingOfflineFormsMessageValue);
    const online = await checkOnlineStatus();

    if (online !== false) {
      this.loadingSpinnerTarget.classList.remove("hidden");
    }

    try {
      const result = await ensureOfflineSanitationFormsReady({
        farmId: this.farmId,
        manifest: this.offlineManifestValue,
        online,
        onSyncStart: (plan) => this.showSyncingStatus(plan),
      });
      this.removeSanitationForms(result.plan?.formsToDelete || []);
      this.renderSanitationForms(result.forms);
      await this.refreshStartButtonsReadiness(result.forms);
      await this.safelyRefreshContinueButtonsReadiness();

      if (result.ready) {
        this.showSyncStatus("success", this.formsReadyOfflineMessageValue);
      } else {
        this.showSyncStatus("warning", this.formsSyncFailedMessageValue);
        if (online === false) alert(this.offlineMessageValue);
      }
    } catch (error) {
      this.showSyncStatus("warning", this.formsSyncFailedMessageValue);
      alert(
        online === false
          ? this.offlineMessageValue
          : this.downloadFailedMessageValue,
      );
      console.error("Error downloading forms:", error);
    } finally {
      this.loadingSpinnerTarget.classList.add("hidden");
    }
  }

  showSyncingStatus(plan) {
    this.showSyncStatus(
      "info",
      this.syncingFormsMessageValue.replace(
        "%{count}",
        syncPlanStaleFormIds(plan).size,
      ),
    );
  }

  showSyncStatus(status, message) {
    if (!this.hasSyncStatusTarget || !this.hasSyncStatusTextTarget) return;

    const alertClass = SYNC_STATUS_CLASSES[status];
    if (!alertClass) return;

    this.syncStatusTarget.classList.remove(
      "hidden",
      ...Object.values(SYNC_STATUS_CLASSES),
    );
    this.syncStatusTarget.classList.add(alertClass);
    this.syncStatusTextTarget.textContent = message;
  }

  removeSanitationForms(forms) {
    forms.forEach((form) => {
      this.listContainerTarget
        .querySelector(`#sanitation_form_${form.id}`)
        ?.remove();
    });
  }

  renderSanitationForms(sanitationForms) {
    sanitationForms.forEach(async (form) => {
      const currentItem = this.listContainerTarget.querySelector(
        `#sanitation_form_${form.id}`,
      );

      if (currentItem) {
        // replace the current item with renderSanitationForm
        currentItem.outerHTML = this.renderSanitationForm(form);
      } else {
        this.listContainerTarget.innerHTML += this.renderSanitationForm(form);
      }
    });
  }

  async renderLocalSanitationRoutes({
    completedSanitationRoutes,
    inProgressSanitationRoutes,
  }) {
    const renderSequence = (this.localRoutesRenderSequence || 0) + 1;
    this.localRoutesRenderSequence = renderSequence;
    this.clearRouteContainers();

    if (
      completedSanitationRoutes.length === 0 &&
      inProgressSanitationRoutes.length === 0
    ) {
      if (!this.localRoutesRenderIsCurrent(renderSequence)) return;
      this.showEmptyRoutesState();
      return;
    }

    await this.renderCompletedSanitationRoutes(
      sortRoutesByRecent(completedSanitationRoutes),
      renderSequence,
    );
    await this.renderInProgressSanitationRoutes(
      sortRoutesByRecent(inProgressSanitationRoutes),
      renderSequence,
    );
    if (!this.localRoutesRenderIsCurrent(renderSequence)) return;

    this.updateRoutesSectionsVisibility(
      completedSanitationRoutes.length,
      inProgressSanitationRoutes.length,
    );
  }

  localRoutesRenderIsCurrent(renderSequence) {
    return this.localRoutesRenderSequence === renderSequence;
  }

  clearRouteContainers() {
    [this.completedRoutesContainer, this.inProgressRoutesContainer].forEach(
      (container) => {
        if (container) container.innerHTML = "";
      },
    );
  }

  async renderInProgressSanitationRoutes(sanitationRoutes, renderSequence = null) {
    const container = this.inProgressRoutesContainer;
    if (!container) {
      DebugLogger.error("Route launcher missing in-progress route container");
      return;
    }

    for (const route of sanitationRoutes) {
      if (
        renderSequence != null &&
        !this.localRoutesRenderIsCurrent(renderSequence)
      ) {
        return;
      }
      const rendered = await this.renderInProgressSanitationRoute(route);
      if (
        renderSequence != null &&
        !this.localRoutesRenderIsCurrent(renderSequence)
      ) {
        return;
      }
      const currentItem = container.querySelector(
        `#sanitation_route_${route.id}`,
      );

      if (currentItem) {
        currentItem.outerHTML = rendered;
      } else {
        const routeElement = document.createElement("div");
        routeElement.innerHTML = rendered;
        container.appendChild(routeElement);
      }
    }
  }

  async renderCompletedSanitationRoutes(sanitationRoutes, renderSequence = null) {
    const container = this.completedRoutesContainer;
    if (!container) {
      DebugLogger.error("Route launcher missing completed route container");
      return;
    }

    for (const route of sanitationRoutes) {
      if (
        renderSequence != null &&
        !this.localRoutesRenderIsCurrent(renderSequence)
      ) {
        return;
      }
      const rendered = await this.renderCompletedSanitationRoute(route);
      if (
        renderSequence != null &&
        !this.localRoutesRenderIsCurrent(renderSequence)
      ) {
        return;
      }
      const currentItem = container.querySelector(
        `#sanitation_route_${route.id}`,
      );

      if (currentItem) {
        currentItem.outerHTML = rendered;
      } else {
        const routeElement = document.createElement("div");
        routeElement.innerHTML = rendered;
        container.appendChild(routeElement);
      }
    }
  }

  get completedRoutesContainer() {
    if (this.hasCompletedRoutesListContainerTarget) {
      return this.completedRoutesListContainerTarget;
    }

    return this.hasSanitationRoutesListContainerTarget
      ? this.sanitationRoutesListContainerTarget
      : null;
  }

  get inProgressRoutesContainer() {
    if (this.hasInProgressRoutesListContainerTarget) {
      return this.inProgressRoutesListContainerTarget;
    }

    return this.hasSanitationRoutesListContainerTarget
      ? this.sanitationRoutesListContainerTarget
      : null;
  }

  progressLabels() {
    return {
      tree: this.progressTreeLabelValue,
      samples: this.progressSamplesLabelValue,
      samplesWithTree: this.progressSamplesWithTreeLabelValue,
      completedReady: this.progressCompletedReadyLabelValue,
    };
  }

  async routeCardContext(route) {
    const [routeDatapoints, sanitationForm] = await Promise.all([
      getRouteDatapoints(route.id),
      getSanitationForm(route.sanitation_form_id),
    ]);

    return { routeDatapoints, sanitationForm };
  }

  async renderInProgressSanitationRoute(route) {
    const template = this.inProgressItemTemplateTarget.innerHTML;
    const { routeDatapoints, sanitationForm } =
      await this.routeCardContext(route);
    const progress = buildInProgressRouteProgress(
      route,
      routeDatapoints,
      sanitationForm,
      this.progressLabels(),
    );

    const rendered = Mustache.render(template, {
      sanitation_form_name: capitalize(route.sanitation_form_name),
      lot_name: capitalize(route.lot_name),
      monitor_name: capitalize(route.sanitation_monitor_name),
      sanitation_route_id: route.id,
      started_at_display: formatRouteStartedAt(
        route.created_at,
        this.farmTimeZone,
      ),
      route_continue_path: await getCurrentDatapointUrl(route.id),
      ...monitorAvatarFields(route.sanitation_monitor_name),
      ...progress,
    });
    return rendered;
  }

  async renderCompletedSanitationRoute(route) {
    const template = this.completedItemTemplateTarget.innerHTML;
    const { routeDatapoints, sanitationForm } =
      await this.routeCardContext(route);
    const progress = buildCompletedRouteProgress(
      route,
      routeDatapoints,
      sanitationForm,
      this.progressLabels(),
    );
    const completedAt = route.completed_at || route.created_at;

    const rendered = Mustache.render(template, {
      sanitation_form_name: capitalize(route.sanitation_form_name),
      lot_name: capitalize(route.lot_name),
      monitor_name: capitalize(route.sanitation_monitor_name),
      sanitation_route_id: route.id,
      completed_at_display: formatRouteStartedAt(
        completedAt,
        this.farmTimeZone,
      ),
      route_continue_path: await getCurrentDatapointUrl(route.id),
      ...monitorAvatarFields(route.sanitation_monitor_name),
      ...progress,
    });
    return rendered;
  }

  async continueInProgressSanitationRoute(event) {
    event.preventDefault();

    const routeId = Number(
      event.target.closest("[data-sanitation-route-id]").dataset
        .sanitationRouteId,
    );
    const currentUrl = await getCurrentDatapointUrl(routeId);
    const navigationResult = await routeNavigationGuard(currentUrl);
    if (!navigationResult.allowed) {
      this.showRouteNotFullyDownloadedMessage();
      return;
    }

    await markSanitationRouteAsActive(routeId);

    const navigationResultAfterMark = await navigateToRouteDocument(currentUrl, {
      historyMode: "push",
    });
    if (!navigationResultAfterMark.allowed) {
      this.showRouteNotFullyDownloadedMessage();
    }
  }

  async deleteLocalSanitationRoute(event) {
    if (event?.preventDefault) event.preventDefault();

    const deleteButton =
      event?.currentTarget || event?.target?.closest(".js-delete-button");
    await this.performLocalSanitationRouteDelete(deleteButton);
  }

  async performLocalSanitationRouteDelete(deleteButton) {
    const routeId = Number(deleteButton?.dataset?.routeId);
    if (!Number.isFinite(routeId)) return;

    if (deleteOperationInProgress || deletingRouteIds.has(routeId)) return;

    deleteOperationInProgress = true;
    deletingRouteIds.add(routeId);
    deleteButton.disabled = true;
    let keepDeleteButtonDisabled = false;
    try {
      const route = await getSanitationRoute(routeId);
      if (!route) {
        this.markRouteCardUploaded(routeId);
        keepDeleteButtonDisabled = true;
        return;
      }

      const confirmed = confirm(this.deleteRouteConfirmationMessage(route));

      if (!confirmed) return;

      await deleteSanitationRoute(routeId);
      await this.loadAndRenderLocalRoutes();
    } finally {
      deleteOperationInProgress = false;
      deletingRouteIds.delete(routeId);
      if (!keepDeleteButtonDisabled && deleteButton.isConnected) {
        deleteButton.disabled = false;
      }
    }
  }

  deleteRouteConfirmationMessage(route) {
    const timestamp =
      route.completed_at || route.last_active_at || route.created_at;
    const timestampLabel =
      route.status === "completed"
        ? this.deleteRouteCompletedAtLabel
        : this.deleteRouteStartedAtLabel;

    return this.deleteRouteConfirmationTemplate
      .replace(
        "%{form_name}",
        this.routeConfirmationValue(route.sanitation_form_name),
      )
      .replace("%{lot_name}", this.routeConfirmationValue(route.lot_name))
      .replace(
        "%{monitor_name}",
        this.routeConfirmationValue(route.sanitation_monitor_name),
      )
      .replace("%{status}", this.deleteRouteStatusLabel(route.status))
      .replace("%{timestamp_label}", timestampLabel)
      .replace(
        "%{timestamp}",
        timestamp
          ? formatRouteStartedAt(timestamp, this.farmTimeZone)
          : this.deleteRouteUnknownValue,
      );
  }

  routeConfirmationValue(value) {
    return value ? capitalize(value) : this.deleteRouteUnknownValue;
  }

  deleteRouteStatusLabel(status) {
    if (status === "completed") return this.deleteRouteCompletedStatus;
    if (status === "in_progress") return this.deleteRouteInProgressStatus;

    return this.routeConfirmationValue(status);
  }

  renderSanitationForm(form) {
    const template = this.listItemTemplateTarget.innerHTML;
    const rendered = Mustache.render(template, {
      sanitation_form_name: capitalize(form.name),
      sanitation_form_id: form.id,
      lot_tree_percentage: form.lot_tree_percentage,
      route_start_path: `/farms/${form.farm_id}/sanitation_forms/${form.id}/first_step`,
    });
    return rendered;
  }

  async startSanitationRoute(event) {
    event.preventDefault();

    const button = event.currentTarget;
    if (button.disabled) return;

    const formId = button.dataset.sanitationFormId;
    const routeStartPath = button.dataset.routeStartPath;

    button.disabled = true;
    setRouteStartButtonLoading(button, true);
    let enableButton = false;
    let navigationTriggered = false;
    // Probe lazily and at most once: a fully-downloaded form starts without
    // any network round trip at all.
    let onlineStatus = null;
    const checkOnline = async () =>
      (onlineStatus ??= await quickCheckOnlineStatus());
    try {
      const result = await ensureOfflineSanitationFormsReady({
        farmId: this.farmId,
        manifest: this.offlineManifestValue,
        formId,
        checkOnline,
        onSyncStart: (plan) => this.showSyncingStatus(plan),
      });

      if (!result.ready) {
        this.showRouteNotFullyDownloadedMessage();
        enableButton = (await checkOnline()) !== false;
        return;
      }

      const navigationResult = await navigateToRouteDocument(routeStartPath, {
        checkOnline,
        historyMode: "push",
      });
      navigationTriggered = navigationResult.allowed;
      enableButton =
        navigationResult.allowed || (await checkOnline()) !== false;
      if (!navigationResult.allowed) {
        this.showRouteNotFullyDownloadedMessage();
      }
    } catch (error) {
      console.error("Error checking form offline readiness:", error);
      const online = await checkOnline();
      if (online !== false) {
        this.showSyncStatus("warning", this.formsSyncFailedMessageValue);
      } else {
        this.showRouteNotFullyDownloadedMessage();
      }
      enableButton = online !== false;
    } finally {
      if (!navigationTriggered) {
        setRouteStartButtonLoading(button, false);
        button.disabled = !enableButton;
      }
    }
  }

  enableStartButtons() {
    this.startButtons.forEach((button) => (button.disabled = false));
  }

  get startButtons() {
    return this.listContainerTarget.querySelectorAll(
      "[data-route-start-path][data-sanitation-form-id]",
    );
  }

  async refreshStartButtonsReadiness(forms = []) {
    const buttons = this.startButtons;
    if (buttons.length === 0) return;

    const onlineStatus = await checkOnlineStatus();

    if (onlineStatus !== false) {
      this.enableStartButtons();
      return;
    }

    await Promise.all(
      Array.from(buttons, async (button) => {
        try {
          const result = await ensureOfflineSanitationFormsReady({
            farmId: this.farmId,
            manifest: this.offlineManifestValue,
            formId: button.dataset.sanitationFormId,
            online: onlineStatus,
          });
          button.disabled = !result.ready;
        } catch (error) {
          console.error("Error checking start route readiness:", error);
          button.disabled = true;
        }
      }),
    );
  }

  async refreshContinueButtonsReadiness() {
    const scope = this.hasLocalRoutesCardBodyTarget
      ? this.localRoutesCardBodyTarget
      : document;
    const buttons = scope.querySelectorAll("[data-route-continue-path]");
    if (buttons.length === 0) return;

    // Cache-first: a cached continuation works online and offline, so the
    // network probe only runs for routes whose document is not cached.
    const uncachedButtons = [];
    await Promise.all(
      Array.from(buttons, async (button) => {
        if (await cachedRouteDocumentExists(button.dataset.routeContinuePath)) {
          button.disabled = false;
        } else {
          uncachedButtons.push(button);
        }
      }),
    );
    if (uncachedButtons.length === 0) return;

    const online = await checkOnlineStatus();
    uncachedButtons.forEach((button) => (button.disabled = online === false));
  }

  async safelyRefreshContinueButtonsReadiness() {
    try {
      await this.refreshContinueButtonsReadiness();
    } catch (error) {
      console.error("Error checking route continuation readiness:", error);
      DebugLogger.warn("Route launcher failed to check continuation readiness", {
        error: error?.message,
      });
    }
  }

  showRouteNotFullyDownloadedMessage() {
    this.showSyncStatus("warning", this.routeNotFullyDownloadedMessage);
  }

  get routeNotFullyDownloadedMessage() {
    return this.hasRouteNotFullyDownloadedMessageValue
      ? this.routeNotFullyDownloadedMessageValue
      : "This route is not fully downloaded. Connect to the internet and download forms again.";
  }

  get resyncingLocalRoutesMessage() {
    return this.hasResyncingLocalRoutesMessageValue
      ? this.resyncingLocalRoutesMessageValue
      : "Checking local route storage...";
  }

  get localRoutesResyncedMessage() {
    return this.hasLocalRoutesResyncedMessageValue
      ? this.localRoutesResyncedMessageValue
      : "Local routes reloaded from this device (%{count} found).";
  }

  get localRoutesNoneFoundMessage() {
    return this.hasLocalRoutesNoneFoundMessageValue
      ? this.localRoutesNoneFoundMessageValue
      : "No local routes were found for this farm on this device.";
  }

  get localRoutesOtherFarmWarningMessage() {
    return this.hasLocalRoutesOtherFarmWarningMessageValue
      ? this.localRoutesOtherFarmWarningMessageValue
      : "%{count} local routes exist on this device, but they belong to another farm. Switch back to that farm to see them.";
  }

  get deleteRouteConfirmationTemplate() {
    return this.hasDeleteRouteConfirmationMessageValue
      ? this.deleteRouteConfirmationMessageValue
      : "Are you sure you want to delete this local route?\n\nRoute: %{form_name}\nLot: %{lot_name}\nMonitor: %{monitor_name}\nStatus: %{status}\n%{timestamp_label}: %{timestamp}\n\nOnly this route saved on this device will be deleted.";
  }

  get deleteRouteInProgressStatus() {
    return this.hasDeleteRouteInProgressStatusValue
      ? this.deleteRouteInProgressStatusValue
      : "In progress";
  }

  get deleteRouteCompletedStatus() {
    return this.hasDeleteRouteCompletedStatusValue
      ? this.deleteRouteCompletedStatusValue
      : "Completed";
  }

  get deleteRouteUnknownValue() {
    return this.hasDeleteRouteUnknownValueValue
      ? this.deleteRouteUnknownValueValue
      : "Unknown";
  }

  get deleteRouteStartedAtLabel() {
    return this.hasDeleteRouteStartedAtLabelValue
      ? this.deleteRouteStartedAtLabelValue
      : "Started";
  }

  get deleteRouteCompletedAtLabel() {
    return this.hasDeleteRouteCompletedAtLabelValue
      ? this.deleteRouteCompletedAtLabelValue
      : "Completed";
  }

  get farmTimeZone() {
    if (this.hasFarmTimeZoneValue && this.farmTimeZoneValue) {
      return this.farmTimeZoneValue;
    }

    return this.offlineManifestValue.farm_time_zone || "UTC";
  }

  get farmId() {
    if (this.hasFarmIdValue) return this.farmIdValue;

    return this.offlineManifestValue.farm_id;
  }

  get serverSanitationForms() {
    return this.offlineManifestValue.sanitation_forms || [];
  }
}
